import React from "react";
import Skeleton from "./Skeleton";
import EmptyState from "./EmptyState";

/* =========================================================
   DataTable
   Generic sortable / paginated table used by:
     - TargetAchievementsAdmin
     - ActivityLogs
     - GlobalDataExplorer
     - TargetManagement

   Search/filter inputs stay in the parent page (every page needs
   different filters) - DataTable only owns rendering, column
   sort-clicks, and pagination controls.

   Props:
     columns: [{ key, label, sortable, width, render(row) }]
     data: array of row objects
     rowKey: string | (row) => string|number
     loading: bool
     emptyText / emptyIcon
     sortBy, sortDir: "ASC" | "DESC"
     onSortChange(key)
     page, limit, total
     onPageChange(nextPage)
========================================================= */
function DataTable({
  columns,
  data = [],
  rowKey = "id",
  loading = false,
  emptyText = "No records found",
  emptyIcon,
  sortBy,
  sortDir = "DESC",
  onSortChange,
  page = 1,
  limit = 20,
  total = 0,
  onPageChange,
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const getKey = (row) => (typeof rowKey === "function" ? rowKey(row) : row[rowKey]);

  // Local text-input state for the "jump to page" box, so the user can type
  // freely before it commits on Enter/blur (rather than firing on every keystroke).
  const [jumpValue, setJumpValue] = React.useState(String(page));

  React.useEffect(() => {
    setJumpValue(String(page));
  }, [page]);

  const commitJump = () => {
    const n = Number(jumpValue);
    if (!Number.isFinite(n)) {
      setJumpValue(String(page));
      return;
    }
    const clamped = Math.min(Math.max(Math.trunc(n), 1), totalPages);
    setJumpValue(String(clamped));
    if (clamped !== page && onPageChange) onPageChange(clamped);
  };

  const handleJumpKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitJump();
    }
  };

  return (
    <div className="data-table-wrap">
      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={col.sortable ? "sortable-th" : ""}
                  onClick={() => col.sortable && onSortChange && onSortChange(col.key)}
                >
                  <span>{col.label}</span>
                  {col.sortable && sortBy === col.key && (
                    <span className="sort-arrow">{sortDir === "ASC" ? " ▲" : " ▼"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading &&
              data.map((row) => (
                <tr key={getKey(row)}>
                  {columns.map((col) => (
                    <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>

        {loading && <Skeleton.Table rows={6} cols={columns.length} />}

        {!loading && data.length === 0 && (
          <EmptyState icon={emptyIcon} title={emptyText} />
        )}
      </div>

      {!loading && total > limit && (
        <div className="data-table-pagination">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange && onPageChange(1)}
            title="First page"
          >
            « First
          </button>
          <button disabled={page <= 1} onClick={() => onPageChange && onPageChange(page - 1)}>
            ‹ Prev
          </button>

          <span className="data-table-pagination-jump">
            Page
            <input
              type="number"
              className="page-jump-input"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={handleJumpKeyDown}
              onBlur={commitJump}
              aria-label="Jump to page"
            />
            of {totalPages} &nbsp;·&nbsp; {total} records
          </span>

          <button
            disabled={page >= totalPages}
            onClick={() => onPageChange && onPageChange(page + 1)}
          >
            Next ›
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => onPageChange && onPageChange(totalPages)}
            title="Last page"
          >
            Last »
          </button>
        </div>
      )}
    </div>
  );
}

export default DataTable;
